import { useCallback, useState } from 'react';
import { MessageError } from '../i18n/errorMessages';
import { HttpError } from '../utils/httpError';

/**
 * Why a read from the server failed. `status` is null when no answer came at
 * all — a lost connection — which is the one case worth simply waiting out.
 */
export interface LoadFailure {
    status: number | null;
    /** The server's own reason, when it gave one. */
    reason: string | null;
}

/**
 * Whatever a read threw, as a {@link LoadFailure}. A wrapped error — the data
 * grid hands its data source's failure on as the `cause` of its own — is
 * unwrapped first.
 */
export function loadFailureOf(err: unknown): LoadFailure {
    if (err instanceof HttpError) {
        return { status: err.status, reason: err.reason };
    }
    if (err instanceof MessageError && typeof err.values.status === 'number') {
        return { status: err.values.status, reason: null };
    }
    if (err instanceof Error && err.cause !== undefined) {
        return loadFailureOf(err.cause);
    }
    return { status: null, reason: null };
}

/**
 * The state behind a {@link LoadErrorAlert}: what went wrong, and a retry that
 * leaves the alert standing — its button spinning — until the next answer is
 * in, so a second failure is not a flicker nobody saw.
 *
 * A screen that reads in an effect puts `attempt` into its dependencies; one
 * that reads some other way calls its own read after `retry()`.
 */
export function useLoadFailure() {
    const [failure, setFailure] = useState<LoadFailure | null>(null);
    const [retrying, setRetrying] = useState(false);
    const [attempt, setAttempt] = useState(0);

    const fail = useCallback((err: unknown) => {
        setFailure(loadFailureOf(err));
        setRetrying(false);
    }, []);
    /** The read came back — or a new list was asked for, which starts clean. */
    const clear = useCallback(() => {
        setFailure(null);
        setRetrying(false);
    }, []);
    const retry = useCallback(() => {
        setRetrying(true);
        setAttempt((count) => count + 1);
    }, []);

    return { failure, retrying, attempt, fail, clear, retry };
}
